import { useCallback, useEffect, useState } from "react";
import {
  Archive, Check, Loader2, Pencil, Plus, RotateCcw, Send, Trash2, X,
} from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/pages/Leads";
import { toast } from "sonner";

const STATUS_TONE = { active: "default", discontinued: "secondary", archived: "outline" };
const OPTION_LISTS = [
  ["colors", "Paint colours"],
  ["pack_sizes", "Pack sizes"],
  ["units", "Units"],
  ["content_units", "Content units"],
];

export default function InventoryCategories({ onChanged }) {
  const [data, setData] = useState(null);
  const [options, setOptions] = useState(null);
  const [requests, setRequests] = useState({ requests: [], can_approve: false, pending: 0 });
  const [uncat, setUncat] = useState([]);
  const [open, setOpen] = useState(null); // 'category' | 'subcategory' | 'request' | 'classify'
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data: c }, { data: o }, { data: r }, { data: u }] = await Promise.all([
        api.get("/inventory/categories"),
        api.get("/inventory/options"),
        api.get("/inventory/master-requests"),
        api.get("/inventory/uncategorised"),
      ]);
      setData(c);
      setOptions(o);
      setRequests(r);
      setUncat(u);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, message) => {
    setBusy(true);
    try {
      await fn();
      if (message) toast.success(message);
      setOpen(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  if (!data || !options) {
    return (
      <div className="flex h-32 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const manage = data.can_manage;

  return (
    <div className="space-y-4" data-testid="inventory-categories">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] text-muted-foreground">
          {manage
            ? "Anything already used in a transaction can be discontinued or archived — never deleted. History always stays."
            : "Pick from these categories when adding items. Ask for a new one and a Director will approve it."}
        </p>
        <div className="flex gap-1.5">
          <Button size="sm" variant="outline" className="gap-1" data-testid="request-master-button"
            onClick={() => {
              setForm({ kind: "item", name: "", category_name: "", subcategory: "", unit: "", reason: "" });
              setOpen("request");
            }}>
            <Send className="h-3.5 w-3.5" /> Ask for new item / category
          </Button>
          {manage && (
            <Button size="sm" className="gap-1" data-testid="add-category-button"
              onClick={() => { setForm({ name: "", fields: [], order: 100 }); setOpen("category"); }}>
              <Plus className="h-3.5 w-3.5" /> Add Category
            </Button>
          )}
        </div>
      </div>

      {manage && requests.requests.some((r) => r.status === "Pending Approval") && (
        <div className="surface p-4" data-testid="pending-master-requests">
          <div className="overline mb-2">Waiting for your approval ({requests.pending})</div>
          {requests.requests.filter((r) => r.status === "Pending Approval").map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-2 text-xs last:border-0">
              <span>
                <Badge variant="outline" className="mr-1 text-[9px] uppercase">{r.kind}</Badge>
                <span className="font-medium">{r.name}</span>
                {r.category_name && <span className="text-muted-foreground"> in {r.category_name}</span>}
                <div className="text-[10px] text-muted-foreground">
                  asked by {r.requested_by_name} • {String(r.requested_at).slice(0, 16).replace("T", " ")}
                  {r.reason ? ` • ${r.reason}` : ""}
                </div>
              </span>
              <span className="flex gap-1">
                <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                  data-testid={`reject-request-${r.id}`}
                  onClick={() => run(() => api.post(`/inventory/master-requests/${r.id}/decision`,
                    { decision: "reject" }), "Request rejected")}>
                  <X className="h-3 w-3" /> No
                </Button>
                <Button size="sm" className="h-7 gap-1 px-2 text-[10px]"
                  data-testid={`approve-request-${r.id}`}
                  onClick={() => run(() => api.post(`/inventory/master-requests/${r.id}/decision`,
                    { decision: "approve" }), "Approved and created")}>
                  <Check className="h-3 w-3" /> Approve
                </Button>
              </span>
            </div>
          ))}
        </div>
      )}

      {!manage && requests.requests.length > 0 && (
        <div className="surface p-4" data-testid="my-master-requests">
          <div className="overline mb-2">My requests</div>
          {requests.requests.map((r) => (
            <div key={r.id} className="flex items-center justify-between border-b border-border py-1.5 text-xs last:border-0">
              <span>{r.name} <span className="text-muted-foreground">({r.kind})</span></span>
              <Badge variant={r.status === "Approved" ? "default" : r.status === "Rejected" ? "destructive" : "secondary"}
                className="text-[9px]">{r.status}</Badge>
            </div>
          ))}
        </div>
      )}

      {manage && uncat.length > 0 && (
        <div className="surface p-4" data-testid="uncategorised-items">
          <div className="overline mb-2">Items with no category yet ({uncat.length})</div>
          <p className="mb-2 text-[11px] text-muted-foreground">
            Nothing is changed until you set one. Stock, rate and history stay exactly as they are.
          </p>
          {uncat.map((i) => (
            <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-border py-1.5 text-xs last:border-0">
              <span>
                {i.name}
                <span className="ml-2 font-mono-num text-muted-foreground">{i.quantity} {i.unit}</span>
              </span>
              <Button size="sm" variant="outline" className="h-7 px-2 text-[10px]"
                data-testid={`classify-item-${i.id}`}
                onClick={() => {
                  setTarget(i);
                  setForm({ category_name: "", subcategory: "" });
                  setOpen("classify");
                }}>
                Set category
              </Button>
            </div>
          ))}
        </div>
      )}

      <div className="grid gap-3 lg:grid-cols-2">
        {data.categories.map((c) => (
          <div key={c.id} className="surface p-4" data-testid={`category-card-${c.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">{c.name}</span>
                  <Badge variant={STATUS_TONE[c.status]} className="text-[9px] uppercase">{c.status}</Badge>
                </div>
                <div className="mt-0.5 text-[11px] text-muted-foreground">
                  {c.item_count} item(s) • {(c.subcategories || []).length} sub-category(ies)
                  {(c.fields || []).length
                    ? ` • asks for ${c.fields.map((f) => data.field_labels[f] || f).join(", ")}`
                    : ""}
                </div>
              </div>
              {manage && (
                <div className="flex flex-wrap gap-1">
                  <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                    data-testid={`edit-category-${c.id}`}
                    onClick={() => {
                      setTarget(c);
                      setForm({ name: c.name, fields: c.fields || [], order: c.order ?? 100 });
                      setOpen("category");
                    }}>
                    <Pencil className="h-3 w-3" /> Rename / fields
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                    data-testid={`add-sub-${c.id}`}
                    onClick={() => { setTarget(c); setForm({ name: "" }); setOpen("subcategory"); }}>
                    <Plus className="h-3 w-3" /> Sub-category
                  </Button>
                  {c.status === "active" ? (
                    <>
                      <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                        data-testid={`discontinue-category-${c.id}`}
                        onClick={() => run(() => api.post(`/inventory/categories/${c.id}/status`,
                          { status: "discontinued" }), `${c.name} discontinued`)}>
                        Discontinue
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                        data-testid={`archive-category-${c.id}`}
                        onClick={() => run(() => api.post(`/inventory/categories/${c.id}/status`,
                          { status: "archived" }), `${c.name} archived`)}>
                        <Archive className="h-3 w-3" /> Archive
                      </Button>
                    </>
                  ) : (
                    <Button size="sm" variant="outline" className="h-7 gap-1 px-2 text-[10px]"
                      data-testid={`reactivate-category-${c.id}`}
                      onClick={() => run(() => api.post(`/inventory/categories/${c.id}/status`,
                        { status: "active" }), `${c.name} is active again`)}>
                      <RotateCcw className="h-3 w-3" /> Reactivate
                    </Button>
                  )}
                  {c.item_count === 0 && (
                    <Button size="sm" variant="ghost" className="h-7 gap-1 px-2 text-[10px] text-destructive"
                      data-testid={`delete-category-${c.id}`}
                      onClick={() => run(() => api.delete(`/inventory/categories/${c.id}`),
                        `${c.name} removed`)}>
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  )}
                </div>
              )}
            </div>

            {(c.subcategories || []).length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`subcategories-${c.id}`}>
                {c.subcategories.map((s) => (
                  <span key={s.name}
                    className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] ${
                      s.status === "active"
                        ? "border-border"
                        : "border-dashed border-border text-muted-foreground line-through"}`}>
                    {s.name}
                    {s.item_count ? <span className="text-muted-foreground">({s.item_count})</span> : null}
                    {manage && (
                      <>
                        <button type="button" title="Rename"
                          data-testid={`rename-sub-${c.id}-${s.name}`}
                          onClick={() => {
                            const next = window.prompt(`Rename “${s.name}” to`, s.name);
                            if (next && next.trim() && next.trim() !== s.name) {
                              run(() => api.put(
                                `/inventory/categories/${c.id}/subcategories/${encodeURIComponent(s.name)}`,
                                { name: next.trim() }), "Sub-category renamed");
                            }
                          }}>
                          <Pencil className="h-2.5 w-2.5" />
                        </button>
                        <button type="button"
                          title={s.status === "active" ? "Discontinue" : "Reactivate"}
                          data-testid={`toggle-sub-${c.id}-${s.name}`}
                          onClick={() => run(() => api.put(
                            `/inventory/categories/${c.id}/subcategories/${encodeURIComponent(s.name)}`,
                            { status: s.status === "active" ? "discontinued" : "active" }),
                            "Sub-category updated")}>
                          {s.status === "active" ? <X className="h-2.5 w-2.5" /> : <RotateCcw className="h-2.5 w-2.5" />}
                        </button>
                      </>
                    )}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {manage && (
        <div className="surface p-4" data-testid="inventory-options">
          <div className="overline mb-2">Colours, pack sizes and units</div>
          <div className="grid gap-3 sm:grid-cols-2">
            {OPTION_LISTS.map(([key, label]) => (
              <Field key={key} label={label}>
                <Input data-testid={`options-${key}-input`}
                  defaultValue={(options[key] || []).join(", ")}
                  onBlur={(e) => {
                    const next = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                    if (next.join(",") !== (options[key] || []).join(",")) {
                      run(() => api.put("/inventory/options", { [key]: next }), `${label} updated`);
                    }
                  }} />
              </Field>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Comma separated. Click away from the box to save.
          </p>
        </div>
      )}

      {/* add / edit category */}
      <Dialog open={open === "category"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{target ? `Edit ${target.name}` : "Add Category"}</DialogTitle>
          </DialogHeader>
          <Field label="Category name">
            <Input data-testid="category-name-input" value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field label="Which extra details should this category ask for?">
            <div className="flex flex-wrap gap-1.5">
              {Object.entries(data.field_labels).map(([key, label]) => {
                const on = (form.fields || []).includes(key);
                return (
                  <button key={key} type="button" data-testid={`category-field-${key}`}
                    onClick={() => setForm({
                      ...form,
                      fields: on ? form.fields.filter((f) => f !== key) : [...(form.fields || []), key],
                    })}
                    className={`rounded-full border px-2 py-0.5 text-[11px] ${
                      on ? "border-primary bg-primary/10 text-primary" : "border-border"}`}>
                    {label}
                  </button>
                );
              })}
            </div>
          </Field>
          <DialogFooter>
            <Button data-testid="category-save-button" disabled={busy}
              onClick={() => run(() => (target
                ? api.put(`/inventory/categories/${target.id}`,
                  { name: form.name, fields: form.fields, order: form.order })
                : api.post("/inventory/categories",
                  { name: form.name, fields: form.fields, order: form.order })),
                target ? "Category updated" : "Category added")}>
              {busy && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* add sub-category */}
      <Dialog open={open === "subcategory"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="subcategory-dialog">
          <DialogHeader>
            <DialogTitle>Add sub-category to {target?.name}</DialogTitle>
          </DialogHeader>
          <Field label="Sub-category name">
            <Input data-testid="subcategory-name-input" value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <DialogFooter>
            <Button data-testid="subcategory-save-button" disabled={busy}
              onClick={() => run(() => api.post(
                `/inventory/categories/${target.id}/subcategories`, { name: form.name }),
                "Sub-category added")}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* staff request */}
      <Dialog open={open === "request"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="master-request-dialog">
          <DialogHeader>
            <DialogTitle>Ask for a new item or category</DialogTitle>
          </DialogHeader>
          <Field label="What do you need?">
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger data-testid="request-kind-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="item">A new item</SelectItem>
                <SelectItem value="subcategory">A new sub-category</SelectItem>
                <SelectItem value="category">A new category</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Name">
            <Input data-testid="request-name-input" value={form.name || ""}
              onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          {form.kind !== "category" && (
            <Field label="Which category does it belong to?">
              <Select value={form.category_name || ""}
                onValueChange={(v) => setForm({ ...form, category_name: v })}>
                <SelectTrigger data-testid="request-category-select">
                  <SelectValue placeholder="Choose category" />
                </SelectTrigger>
                <SelectContent>
                  {data.categories.filter((c) => c.status === "active").map((c) => (
                    <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
          {form.kind === "item" && (
            <Field label="Unit (optional)">
              <Input data-testid="request-unit-input" placeholder="e.g. Piece, Kg, Litre"
                value={form.unit || ""} onChange={(e) => setForm({ ...form, unit: e.target.value })} />
            </Field>
          )}
          <Field label="Why do you need it?">
            <Input data-testid="request-reason-input" value={form.reason || ""}
              onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          </Field>
          <DialogFooter>
            <Button data-testid="request-submit-button" disabled={busy}
              onClick={() => run(() => api.post("/inventory/master-requests", form),
                "Sent to the Director for approval")}>
              Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* classify an existing item */}
      <Dialog open={open === "classify"} onOpenChange={(o) => !o && setOpen(null)}>
        <DialogContent data-testid="classify-dialog">
          <DialogHeader>
            <DialogTitle>Set category — {target?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-[11px] text-muted-foreground">
            Only the category is saved. Stock, rate, transactions and history are untouched.
          </p>
          <Field label="Category">
            <Select value={form.category_name || ""}
              onValueChange={(v) => setForm({ ...form, category_name: v, subcategory: "" })}>
              <SelectTrigger data-testid="classify-category-select">
                <SelectValue placeholder="Choose category" />
              </SelectTrigger>
              <SelectContent>
                {data.categories.filter((c) => c.status === "active").map((c) => (
                  <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          {(() => {
            const cat = data.categories.find((c) => c.name === form.category_name);
            const subs = (cat?.subcategories || []).filter((s) => s.status === "active");
            if (!subs.length) return null;
            return (
              <Field label="Sub-category">
                <Select value={form.subcategory || ""}
                  onValueChange={(v) => setForm({ ...form, subcategory: v })}>
                  <SelectTrigger data-testid="classify-subcategory-select">
                    <SelectValue placeholder="Choose sub-category" />
                  </SelectTrigger>
                  <SelectContent>
                    {subs.map((s) => <SelectItem key={s.name} value={s.name}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </Field>
            );
          })()}
          <DialogFooter>
            <Button data-testid="classify-save-button" disabled={busy}
              onClick={() => run(() => api.put(`/inventory/items/${target.id}/classification`, {
                category_name: form.category_name,
                subcategory: form.subcategory || "",
              }), "Category set")}>
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
